/**
 * How the editor reaches an engine view: a {@link ViewChannel}.
 *
 * - **Worker** (default): the view runs in the engine worker
 *   (engine-worker.ts) and draws to the canvas transferred to it as an
 *   `OffscreenCanvas`. Loading a scene, starting Play or a slow frame then
 *   only occupies the worker; the editor UI on the page keeps working.
 * - **Page** (fallback): the view runs on the page's thread, as before.
 *   Used when the browser cannot hand WebGPU canvases to a worker, or with
 *   `localStorage["prism.engineOnPage"] = "1"` (debugging).
 *
 * Both answer every call asynchronously, so the editor code is the same.
 * Calls are delivered in order; `send` is fire-and-forget.
 */

import type { PrismHost, PrismMode } from './prism.js';

export interface ViewChannel {
  /** The view runs in the engine worker. */
  readonly remote: boolean;
  /** PrismView methods this engine build has. */
  readonly methods: ReadonlySet<string>;
  call<T>(method: string, args?: unknown[], transfer?: Transferable[]): Promise<T>;
  send(method: string, args?: unknown[], transfer?: Transferable[]): void;
  /** The engine trapped (every view of it is gone); set by EngineView. */
  onCrash: ((message: string) => void) | null;
  free(): void;
}

/* ------------------------------------------------------------------ */
/* Page channel                                                        */

/** A PrismView on this page. Synchronous engine errors become rejections. */
export function pageChannel(api: object): ViewChannel {
  const record = api as Record<string, unknown>;
  const methods = new Set<string>();
  // wasm-bindgen classes keep their methods on the prototype; plain objects (tests) on themselves.
  for (let o: object | null = api; o && o !== Object.prototype; o = Object.getPrototypeOf(o) as object | null) {
    for (const name of Object.getOwnPropertyNames(o)) if (name !== 'constructor' && typeof record[name] === 'function') methods.add(name);
  }
  const invoke = (method: string, args: unknown[]): unknown => {
    const fn = record[method];
    if (typeof fn !== 'function') throw new Error(`The engine has no ${method}()`);
    return (fn as (...a: unknown[]) => unknown).apply(api, args);
  };
  return {
    remote: false,
    methods,
    onCrash: null,
    // Runs at once (keeps the order with `send`); a thrown error rejects.
    call: <T>(method: string, args: unknown[] = []) => new Promise<T>((resolve) => resolve(invoke(method, args) as T)),
    send: (method, args = []) => {
      invoke(method, args);
    },
    free: () => {
      try {
        invoke('free', []);
      } catch {
        // Already freed by the engine.
      }
    },
  };
}

/* ------------------------------------------------------------------ */
/* Worker channel                                                      */

interface WorkerCaps {
  gpu: boolean;
  raf: boolean;
  offscreen: boolean;
}

export interface WorkerStart {
  ok: boolean;
  /** The engine bundle is not built. */
  missing?: boolean;
  error?: string;
  caps?: WorkerCaps;
}

type Outbound =
  | ({ type: 'ready' } & WorkerStart)
  | { type: 'reply'; seq: number; ok: boolean; value?: unknown; error?: string }
  | { type: 'event'; view: number; json: string }
  | { type: 'host'; seq: number; view: number; fn: string; args: unknown[] }
  | { type: 'writeCache'; view: number; path: string; bytes: Uint8Array }
  | { type: 'crash'; message: string };

let worker: Worker | null = null;
let started: Promise<WorkerStart> | null = null;
let crashMessage: string | null = null;
/** The worker started but its engine did not load (e.g. not built yet). */
let startFailed = false;
let nextSeq = 1;
let nextView = 1;
const pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
const hosts = new Map<number, PrismHost>();
const channels = new Map<number, ViewChannel>();

/** Whether this page can run views in the worker at all (before starting it). */
export function workerPossible(): boolean {
  try {
    if (globalThis.localStorage?.getItem('prism.engineOnPage') === '1') return false;
  } catch {
    // Storage blocked: use the worker.
  }
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    'transferControlToOffscreen' in HTMLCanvasElement.prototype
  );
}

/**
 * Starts the engine worker once; resolves when the engine module is loaded
 * there. A crashed worker has no live views left (the crash took them all),
 * so a new view replaces it with a fresh worker instead of failing until the
 * page is reloaded.
 */
export function startEngineWorker(url: string): Promise<WorkerStart> {
  if (crashMessage) resetEngineWorker();
  if (started) return started;
  started = new Promise<WorkerStart>((done) => {
    const resolve = (start: WorkerStart) => {
      startFailed = !start.ok;
      done(start);
    };
    try {
      worker = new Worker(new URL('./engine-worker.ts', import.meta.url), { type: 'module', name: 'prism-engine' });
    } catch (error) {
      resolve({ ok: false, error: `could not start the engine worker: ${error instanceof Error ? error.message : String(error)}` });
      return;
    }
    worker.onmessage = (event: MessageEvent<Outbound>) => {
      const msg = event.data;
      if (msg.type === 'ready') resolve(msg);
      else onMessage(msg);
    };
    worker.onerror = (event) => {
      // A failure to even load the worker script (before `ready`).
      resolve({ ok: false, error: `engine worker failed: ${event.message || 'script error'}` });
    };
    worker.postMessage({ type: 'init', url: new URL(url, location.href).href });
  });
  return started;
}

/**
 * A crashed worker, or one whose engine failed to load (not built yet), is
 * replaced by a fresh one on the next view (Retry).
 */
export function resetEngineWorker(): void {
  if (!crashMessage && !startFailed) return;
  startFailed = false;
  worker?.terminate();
  worker = null;
  started = null;
  crashMessage = null;
  pending.clear();
  hosts.clear();
  channels.clear();
}

function post(message: unknown, transfer: Transferable[] = []): void {
  worker?.postMessage(message, transfer);
}

function request<T>(message: Record<string, unknown>, transfer: Transferable[] = []): Promise<T> {
  if (crashMessage) return Promise.reject(new Error(crashMessage));
  if (!worker) return Promise.reject(new Error('The engine worker is not running'));
  const seq = nextSeq++;
  return new Promise<T>((resolve, reject) => {
    pending.set(seq, { resolve: resolve as (value: unknown) => void, reject });
    post({ ...message, seq }, transfer);
  });
}

function onMessage(msg: Outbound): void {
  switch (msg.type) {
    case 'reply': {
      const p = pending.get(msg.seq);
      pending.delete(msg.seq);
      if (msg.ok) p?.resolve(msg.value);
      else p?.reject(new Error(msg.error ?? 'engine call failed'));
      break;
    }
    case 'event':
      hosts.get(msg.view)?.onEvent(msg.json);
      break;
    case 'writeCache':
      hosts.get(msg.view)?.writeCache?.(msg.path, msg.bytes);
      break;
    case 'host':
      void runHost(msg);
      break;
    case 'crash': {
      crashMessage = msg.message;
      for (const p of pending.values()) p.reject(new Error(msg.message));
      pending.clear();
      for (const ch of channels.values()) ch.onCrash?.(msg.message);
      break;
    }
    case 'ready':
      break;
  }
}

async function runHost(msg: Extract<Outbound, { type: 'host' }>): Promise<void> {
  const host = hosts.get(msg.view) as unknown as Record<string, unknown> | undefined;
  const fn = host?.[msg.fn];
  try {
    if (typeof fn !== 'function') throw new Error(`host.${msg.fn} is not available`);
    const value: unknown = await (fn as (...a: unknown[]) => unknown).apply(host, msg.args);
    const transfer: Transferable[] = [];
    let out = value;
    if (value instanceof Uint8Array) {
      // Transfer a private copy: the host may keep its own (memory cache, overlay).
      out = value.slice();
      transfer.push((out as Uint8Array).buffer);
    }
    post({ type: 'hostReply', seq: msg.seq, ok: true, value: out }, transfer);
  } catch (error) {
    post({ type: 'hostReply', seq: msg.seq, ok: false, error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Creates a view in the engine worker drawing to `canvas`, which is
 * transferred (it can never be drawn by the page again: use a fresh canvas
 * for every view).
 */
export async function workerChannel(canvas: HTMLCanvasElement, mode: PrismMode, host: PrismHost): Promise<ViewChannel> {
  const offscreen = canvas.transferControlToOffscreen();
  const view = nextView++;
  hosts.set(view, host);
  const available = (['writeCache', 'loadSaves', 'writeSave', 'deleteSave'] as const).filter((fn) => typeof host[fn] === 'function');
  let names: string[];
  try {
    names = await request<string[]>({ type: 'create', view, mode, canvas: offscreen, host: available, account: host.account ?? null }, [offscreen]);
  } catch (error) {
    hosts.delete(view);
    throw error;
  }
  let freed = false;
  const channel: ViewChannel = {
    remote: true,
    methods: new Set(names),
    onCrash: null,
    call: <T>(method: string, args: unknown[] = [], transfer: Transferable[] = []) =>
      freed ? Promise.reject(new Error('Viewport has been freed')) : request<T>({ type: 'call', view, method, args }, transfer),
    send: (method, args = [], transfer = []) => {
      if (!freed && !crashMessage) post({ type: 'call', view, method, args }, transfer);
    },
    free: () => {
      if (freed) return;
      freed = true;
      post({ type: 'free', view });
      hosts.delete(view);
      channels.delete(view);
    },
  };
  channels.set(view, channel);
  return channel;
}
